<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch} from "vue";
import router from "@/router";
import {useRoute} from "vue-router";
import YoutubeVideoService from "@/services/YoutubeVideoService";
import YoutubeChannelService from "@/services/YoutubeChannelService";
import type {YoutubeChannel} from "@/models/youtube_channel";
import type {YoutubeVideo} from "@/models/youtube_video";
import mdmService from "@/services/MdmService";
import {store} from "@/store";
import {RouteNames} from "@/router/routeNames";
import Bugsnag from "@bugsnag/js";
import {useToast} from "vue-toastification";
import debounce from "lodash/debounce";

const route = useRoute();
const toast = useToast();

let channelID = '';
let showPurplePlayer = ref(true);

const channel = ref<YoutubeChannel>({
  channel_id: '',
  name: '',
  image_url: '',
  tags: [],
});
const videos = ref<YoutubeVideo[]>([]);

const searchQuery = ref<string>('');
const searched = ref<boolean>(false);

const filters = ref<any>({
  page_size: 10,
  page: 0,
  search: '',
})

const loading = ref<boolean>(false);
const noMoreVideos = ref(false);
const error = ref<any>();
const debouncedHandleScroll = debounce(handleScroll, 200);

onMounted(() => {
  channelID = route.params.channelId as string;
  showPurplePlayer.value = store.showPurplePlayer.value;
  initHomeView();
  window.addEventListener("scroll", debouncedHandleScroll);
})

onUnmounted(() => {
  window.removeEventListener("scroll", debouncedHandleScroll);
})

async function initHomeView() {
  try {
    await getChannel();
    await mdmService.sendActivityLog('ViewedChannel', channel.value.name);
    await autoLoadUntilScrollable();
  } catch (err: any) {
    error.value = err;
  }
}

async function getChannel() {
  try {
    channel.value = await YoutubeChannelService.get(channelID);
  } catch (err: any) {
    console.error("Error in getChannel: ", err);
    toast.error("An unexpected error occurred while fetching channel. Please refresh the page and try again.");
    Bugsnag.notify(err);
    throw err;
  }
}

async function loadMoreVideos() {
  if (loading.value || noMoreVideos.value) {
    return;
  }
  loading.value = true;

  // Save current page so we can roll back on error if needed.
  const currentPage = filters.value.page;

  try {
    const res = await YoutubeVideoService.list(channelID, filters.value);
    if (res.data.length === 0) {
      noMoreVideos.value = true;
      return;
    }
    videos.value.push(...res.data);
    filters.value.page = currentPage + 1;
  } catch (err: any) {
    console.error("Error in loadMoreVideos:", err);
    // Roll back to the original page number if there's an error.
    filters.value.page = currentPage;
    toast.error("An error occurred while loading videos. Please try again.");
    Bugsnag.notify(err);
  } finally {
    loading.value = false;
  }
}

async function autoLoadUntilScrollable() {
  // Load one page.
  await loadMoreVideos();
  // If the content is still too short to allow scrolling, keep loading.
  while (document.documentElement.offsetHeight <= window.innerHeight && !noMoreVideos.value) {
    await loadMoreVideos();
  }
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString();
}

function searchVideos(searchQuery: string) {
  filters.value.search = searchQuery;
  filters.value.page = 0;
  searched.value = true;
  videos.value = [];
  noMoreVideos.value = false;
  mdmService.sendActivityLog('SearchedVideos', searchQuery);
  autoLoadUntilScrollable();
}

function clearSearch() {
  searchQuery.value = '';
  if (!searched.value) {
    return;
  }
  filters.value.search = '';
  filters.value.page = 0;
  videos.value = [];
  noMoreVideos.value = false;
  searched.value = false;
  autoLoadUntilScrollable();
}

async function goToVideoPage(videoId: string) {
  await router.push({
    name: RouteNames.Video,
    params: {
      videoId: videoId,
    },
    query: {
      showPurplePlayer: showPurplePlayer.value.toString(),
    }
  });
}

watch(searchQuery, (newValue, oldValue) => {
  if (!newValue && oldValue) {
    clearSearch();
  }
})

function handleScroll() {
  // First, we calculate the current scroll position. We use Math.max to ensure
  // we get the highest value among the different properties that might represent
  // the scroll position across browsers.
  const scrollPosition = Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
  );

  // Next, we get the height of the browser's viewport (visible part of the page)
  const windowHeight = window.innerHeight;

  // Then, we determine the total height of the document (the entire content height)
  const documentHeight = document.documentElement.offsetHeight;

  // Return early if the user hasn't reached within 200 pixels of the bottom
  if (scrollPosition + windowHeight < documentHeight - 200) {
    return;
  }

  // If we get here, the threshold is reached
  loadMoreVideos();
}

function toggleShowPurplePlayer() {
  showPurplePlayer.value = !showPurplePlayer.value;
  store.setShowPurplePlayer(showPurplePlayer.value);
}
</script>

<template>
  <RouterLink :to="{name: RouteNames.Channels}" class="back-link">← All channels</RouterLink>

  <div class="info-container" v-if="channel">
    <div class="channel-info" v-if="channel">
      <img class="channel-image" :src="channel.image_url" :alt="channel.name ?? ''"
           v-if="channel.image_url"/>
      <span class="channel-title">{{ channel.name }}</span>
    </div>

    <div class="branding-info" v-if="channel" @click="toggleShowPurplePlayer">
      <img class="branding-image" :class="{'branding-image-filter' : !showPurplePlayer}"
           src="@/assets/made_purple_logo.png" alt="Made Purple Ltd">
      <span class="branding-title">powered by Made Purple</span>
    </div>
  </div>

  <div class="search-bar-wrapper">
    <form class="search-container" @submit.prevent="searchVideos(searchQuery)">
      <input type="text" v-model="searchQuery" placeholder="Search videos"/>
      <button type="submit" :disabled="searchQuery.trim().length <= 0">Search</button>
      <button class="clear-search" v-if="searchQuery.trim().length >= 1" @click="clearSearch">Clear</button>
    </form>
  </div>

  <template v-if=" error">
    <h1 class="text-message">
      An unexpected error occurred. Please refresh the page and try again.
    </h1>
  </template>

  <template v-else-if="!loading && searched && videos.length <= 0">
    <h1 class="text-message">No results found</h1>
  </template>

  <template v-else>
    <div class="grid-container">
      <div v-for="(video, index) in videos" :key="index" class="grid-item">
        <img :src="video.thumbnail" :alt="video.title"
             @click="goToVideoPage(video.video_id)"/>
        <div class="video-info">
          <div class="video-title" @click="goToVideoPage(video.video_id)">
            {{ video.title }}
          </div>
          <div class="video-date">
            {{ formatDate(video.published_at) }}
          </div>
        </div>
      </div>
    </div>
  </template>

  <div class="loading-bar-bottom" v-if="loading && !error">
    <div class="loading-bar-progress"></div>
  </div>
</template>

<style scoped>
.back-link {
  display: inline-block;
  padding: 0 16px;
  font-weight: bold;
  color: var(--color-text);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

.info-container {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.channel-info {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin-bottom: 16px;
}

.channel-image {
  width: 128px;
  height: 128px;
  border-radius: 25%;
  object-fit: cover;
  margin-right: 16px;
}

.channel-title {
  font-size: 24px;
  font-weight: bold;
}

.branding-info {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin-bottom: 16px;
}

.branding-image {
  width: 76px;
  height: 76px;
  object-fit: contain;
  margin-right: 16px;
}

.branding-image-filter {
  filter: grayscale(100%);
}

.branding-title {
  font-size: 12px;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 16px;
  padding: 16px;
  max-width: 100%;
  margin: 0 auto;
}

.grid-item {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.grid-item img {
  object-fit: cover;
  width: 100%;
  border-radius: 16px;
  margin-bottom: 8px;
}

.grid-item img:hover {
  cursor: pointer;
}

.video-info {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
  padding-right: 8px
}

.video-title {
  font-size: 16px;
  font-weight: bold;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;
}

.video-title:hover {
  cursor: pointer;
}

.video-date {
  font-size: 14px;
  color: #999;
}

/* Media query for 3 columns */
@media (max-width: 1024px) {
  .grid-container {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Media query for 2 columns */
@media (max-width: 768px) {
  .grid-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Media query for 1 column */
@media (max-width: 512px) {
  .grid-container {
    grid-template-columns: 1fr;
  }
}

.search-bar-wrapper {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: var(--color-background); /* Add the background color of your app or page */
  padding-bottom: 8px;
  padding-top: 8px;
  margin-bottom: 8px;
}

.search-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 16px;
  margin-right: 16px;
}

.search-container input {
  flex: 1;
  padding: 8px;
  border: 1px solid #cccccc;
  border-radius: 4px;
  font-size: 14px;
}

.search-container input:focus {
  outline: none;
}

.search-container button {
  border: 1px solid #cccccc;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  margin-left: 8px;
}

.search-container button:disabled {
  background-color: #343434 !important;
  cursor: not-allowed !important;
}

.clear-search {
  margin-left: 8px;
  background-color: grey !important;
}

button {
  background-color: red !important;
  border: none !important;
  color: white !important;
  font-weight: bold;
}

button:disabled {
  background-color: darkgray !important;
}

button:not(:disabled):hover {
  background-color: darkred !important;
}

.text-message {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
</style>

